import { decimal, label, projectDate } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Button } from '@shared/ui/Button';
import { KspSummary } from '@widgets/ksp-workspace/Summary';
import { Card, Muted, Notice, Small } from '../styles';
import { choice, dateField, rdCatalogue, textField } from '../types';

export interface OverviewTabProps {
  workspace: KspWorkspace;
  projectId: string;
  versionId?: string | null;
  timezone: string;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  proposal: {
    id: string;
    action: string;
    text: string;
    refs: string[];
  } | null;
  setProposal: (
    proposal: {
      id: string;
      action: string;
      text: string;
      refs: string[];
    } | null,
  ) => void;
}

export function OverviewTab({
  workspace: d,
  projectId,
  versionId,
  timezone,
  permission,
  open,
  execute,
  proposal,
  setProposal,
}: OverviewTabProps) {
  return (
    <>
      <KspSummary data={d.summary} />
      <Card>
        <h3>Помощник инженера КСП</h3>
        <Button
          disabled={!permission() || !d.summary}
          onClick={() =>
            open(
              'Подготовить предложение инженеру',
              [
                {
                  ...textField('question', 'Вопрос и подтверждённые обстоятельства'),
                  type: 'textarea',
                },
                choice(
                  'action',
                  'Результат',
                  [
                    { value: 'explanation', label: 'Пояснение и вопросы для проверки' },
                    {
                      value: 'period.create',
                      label: 'Предложение месячно-суточного задания',
                    },
                  ],
                  'explanation',
                ),
                {
                  ...dateField('start_date', 'Начало предлагаемого периода'),
                  required: false,
                },
                {
                  ...dateField('end_date_exclusive', 'Конец периода (не включается)'),
                  required: false,
                },
                choice(
                  'quantity_precision',
                  'Точность количества',
                  [
                    { value: '2', label: '0.01' },
                    { value: '3', label: '0.001' },
                    { value: '8', label: '0.00000001' },
                  ],
                  '8',
                ),
              ],
              async (values) => {
                const saved = (await execute('assistant/proposals', {
                  project_id: projectId,
                  question: values.question,
                  action: values.action,
                  command: {
                    start_date: values.start_date,
                    end_date_exclusive: values.end_date_exclusive,
                    version_id: versionId,
                    quantity_precision: Number(values.quantity_precision),
                  },
                })) as
                  | {
                      id?: string;
                      payload?: {
                        _proposal_context?: { explanation?: string; source_refs?: string[] };
                      };
                    }
                  | undefined;
                const context = saved?.payload?._proposal_context;
                if (typeof saved?.id === 'string')
                  setProposal({
                    id: saved.id,
                    action: values.action,
                    text:
                      context?.explanation ??
                      'Предложение сохранено. Перед применением проверьте исходные данные.',
                    refs: context?.source_refs ?? [],
                  });
              },
            )
          }
        >
          Подготовить предложение
        </Button>
        {proposal && (
          <Card>
            <Muted>{proposal.text}</Muted>
            {proposal.refs.map((ref) => (
              <Small key={ref}>{ref}</Small>
            ))}
            {proposal.action !== 'explanation' && (
              <Button
                disabled={!permission()}
                onClick={() =>
                  open(
                    'Применить проверенное предложение',
                    [textField('basis', 'Решение ответственного инженера')],
                    (values) => execute(`proposals/${proposal.id}/apply`, values),
                  )
                }
              >
                Применить проверенное предложение
              </Button>
            )}
          </Card>
        )}
        <Small>
          Числа, даты и ресурсы рассчитывает сервер. Гипотезы требуют проверки; применение
          проходит обычные права проекта.
        </Small>
      </Card>
      <Card>
        <h3>Источники и полнота</h3>
        {d.rd_count_coverage && (
          <Notice>
            Подтверждённых комплектов РД: {d.rd_count_coverage.received_count} из{' '}
            {d.rd_count_coverage.expected_count ?? 'неизвестного перечня'} · полнота{' '}
            {decimal(d.rd_count_coverage.coverage_pct, ' %')}.
            {d.rd_count_coverage.missing_codes.length > 0 &&
              ` Не получены: ${d.rd_count_coverage.missing_codes.join(', ')}.`}
          </Notice>
        )}
        {d.source_health?.map((source) => (
          <Muted key={source.source}>
            {label(source.source)}: {label(source.status)} ·{' '}
            {source.as_of ? projectDate(source.as_of, timezone) : 'обновление неизвестно'} ·{' '}
            {source.reason}
          </Muted>
        ))}
        <Button
          disabled={!permission('manage')}
          onClick={() =>
            open(
              'Настройки проекта',
              [
                textField('timezone', 'Часовой пояс IANA', true, d.settings.timezone),
                {
                  ...textField(
                    'notification_time',
                    'Время ежедневного сообщения',
                    true,
                    d.settings.daily_notice_time,
                  ),
                  type: 'time',
                },
                textField('customer_name', 'Заказчик', true, d.settings.customer),
                textField('organization_name', 'Отправитель', true, d.settings.sender),
                {
                  ...textField(
                    'expected_rd_codes',
                    'Ожидаемые шифры комплектов РД (по одному в строке)',
                    false,
                    (
                      d.settings.expected_rd_codes ??
                      d.settings.settings?.expected_rd_codes ??
                      []
                    ).join('\n'),
                  ),
                  type: 'textarea',
                  help: 'Утверждённый перечень. Пустое поле сохраняет неизвестную полноту; процент рассчитывается по документам ПТО.',
                },
                textField(
                  'expected_rd_basis',
                  'Основание ожидаемого перечня РД',
                  false,
                  d.settings.expected_rd_basis ??
                    d.settings.settings?.expected_rd_basis ??
                    '',
                ),
                choice(
                  'actual_source_policy',
                  'Источник факта по умолчанию',
                  [
                    { value: 'field', label: 'Прораб' },
                    { value: 'pto', label: 'ПТО' },
                    { value: 'explicit_split', label: 'Явное разделение' },
                  ],
                  d.settings.actual_source_policy,
                ),
              ],
              (values) =>
                execute(
                  `projects/${projectId}/settings`,
                  { ...values, ...rdCatalogue(values) },
                  'PATCH',
                ),
            )
          }
        >
          Настроить проект
        </Button>
        <Small>
          Окно обязательного предупреждения: 14 календарных дней до договорной даты.
        </Small>
      </Card>
    </>
  );
}
